import { ChevronDown } from "lucide-react";
import { useState, type FC } from "react";

import { cn } from "@/lib/utils";

import type { Artifact } from "../../caseIde/types";
import type { InsightsFlow } from "../hooks/useInsightsFlow";
import type { KeyFactActions } from "../hooks/useKeyFactActions";
import { groupTone, instanceLabel, instanceSummary } from "../model";
import type { KeyFact } from "../types";
import { FactRow } from "./FactRow";

/**
 * The instances of one kind (a venue ranking per publication, a remuneration
 * per pay record) under one collapsible row with a count summary; each opens
 * as its own fact row. It opens by itself when one of them is the ask in focus.
 */
export const InstancesRow: FC<{
  individualId: string;
  label: string;
  facts: KeyFact[];
  flow: InsightsFlow;
  actions: KeyFactActions;
  onOpen: (artifact: Artifact) => void;
}> = ({ individualId, label, facts, flow, actions, onOpen }) => {
  const [expanded, setExpanded] = useState(false);
  const attention = groupTone(facts) === "attention";
  const holdsCurrent = facts.some((f) => f.key === flow.current?.key);
  const open = expanded || holdsCurrent;
  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setExpanded(!open)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-fill-1"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-fg-3">{label}</span>
          <span
            className={cn(
              "mt-0.5 block truncate text-md",
              attention ? "text-warn" : "text-fg"
            )}
          >
            {instanceSummary(facts)}
          </span>
        </span>
        <ChevronDown
          className={cn(
            "mt-1 size-3.5 text-fg-4 transition-transform",
            open && "rotate-180"
          )}
          aria-hidden
        />
      </button>
      {open ? (
        <ul className="ml-4 divide-y divide-line-1 border-l border-line-1">
          {facts.map((fact) => (
            <FactRow
              key={fact.key}
              individualId={individualId}
              fact={fact}
              label={instanceLabel(fact)}
              flow={flow}
              actions={actions}
              onOpen={onOpen}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};
